// What the Insights tab says, as arithmetic. Pure — no Vue, no DOM, no store
// and no clock of its own.
//
// TWO SOURCES, ONE SCREEN. The money comes from PLACED bets (`betRow`'s rows,
// with the frozen multi and its legs beside them), and everything about how the
// pad has actually performed comes from the PAPER TRAIL, aggregated per
// strategy key. They answer different questions and neither is a substitute for
// the other: the money is what the punter did, the paper is what the pad
// proposed whether or not a cent went on it — which is the larger and more
// honest sample, and the only one that can say anything about an edge.
//
// WHICH OF THE TWO THE MONEY HALF READS IS THE MODE — see `MODES` below. Both
// records are on this screen in either mode; what the mode decides is which one
// the hero, the curve and the scoreboard are ABOUT.
//
// A STRATEGY KEY is `strategyId`, except Classic, which is split into its four
// tiers (`classic-four-ways#low`). A low-risk double and a six-leg outrageous
// are not one strategy with a wide variance; they are four different bets the
// same pad prints, and averaging them together is how a scoreboard stops saying
// anything. See `keyFor`.
//
// AND THE GROUPING IS OFF THE MULTI, NEVER OFF THE BET RECORD.
// `bet-record.strategyId` is wrong on every row written before the strategy
// engine — the migration stamped them all Classic — so `betRow` reads
// `multi.strategyId` and this file reads `row.strategyId`, which is that.

import { shiftDate, verdictFrom, verdictText } from './paper-trail.js';
import { meanInterval } from './engine/metrics.js';
import { canonicalStrategyId } from './engine/strategies/aliases.js';
import { dayShort, money, pct, signedMoney, signedPct, NO_VALUE } from './format.js';

/**
 * WHICH RECORD THE MONEY HALF READS. The screen used to be one answer — the
 * money was the placed bets and nothing else, and the paper trail only reached
 * the small tiles under it. That made the larger and more honest half of the
 * record unreadable on the screen built to read it: a fortnight of backing four
 * tickets a week is thirty-odd bets, and the pad's own record over the same
 * fortnight is every ticket it printed.
 *
 * So the mode is a first-class reading of the whole screen, and PROPOSED is the
 * default: the paper trail is what the app is for.
 */
export const MODES = Object.freeze([
  { value: 'proposed', label: 'Proposed', title: 'Proposed' },
  { value: 'placed', label: 'Placed', title: 'Placed' },
]);

/** The mode a value names, falling back to Proposed rather than to nothing. */
export const modeSpec = (value) => MODES.find((m) => m.value === value) ?? MODES[0];

/** One line under the segment saying what the figures above it are counting. */
export const MODE_EXPLAINER = Object.freeze({
  proposed: 'Every ticket the pad proposed, at its printed stake, placed or not',
  placed: 'Only the bets you marked as placed',
});

/** Whose stakes the money is measured at. Printed in the hero's sub line. */
const STAKE_NOTE = Object.freeze({
  proposed: 'at printed stakes',
  placed: 'at your own stakes',
});

/** The windows the segment offers. `days: null` is all of it. */
export const RANGES = Object.freeze([
  { value: '7d', label: '7d', days: 7, title: 'Last 7 days' },
  { value: '30d', label: '30d', days: 30, title: 'Last 30 days' },
  { value: 'all', label: 'All', days: null, title: 'All time' },
]);

/** The range a value names, falling back to the default rather than to nothing. */
export const rangeSpec = (value) => RANGES.find((r) => r.value === value) ?? RANGES[1];

const round2 = (n) => Math.round(n * 100) / 100;

/**
 * Which days a range covers, and which days the one before it covered.
 *
 * Calendar arithmetic through `shiftDate`, never milliseconds: a window is a
 * run of pad days, and 24-hour arithmetic lands on the wrong one either side of
 * a daylight-saving change.
 *
 * `from` and `prevFrom` are null on All — there is no "before all time", which
 * is exactly why the delta chip is withheld there rather than printed as zero.
 */
export function windowFor(range, today) {
  const spec = rangeSpec(range);
  if (!spec.days) {
    return { range: spec.value, days: null, from: null, to: today, prevFrom: null, prevTo: null, title: spec.title, priorLabel: null };
  }
  const from = shiftDate(today, -(spec.days - 1));
  const prevTo = shiftDate(from, -1);
  return {
    range: spec.value,
    days: spec.days,
    from,
    to: today,
    prevFrom: shiftDate(prevTo, -(spec.days - 1)),
    prevTo,
    title: spec.title,
    priorLabel: `the ${spec.days} days before`,
  };
}

/** Is a 'YYYY-MM-DD' inside [from, to]? An absent `from` is "everything up to `to`". */
export const withinDay = (date, from, to) => {
  if (!date) return false;
  if (from && date < from) return false;
  return !(to && date > to);
};

/** The entries a window holds, by the day the bet was FOR — never the day it was placed. */
export const inWindow = (entries, w) =>
  (entries ?? []).filter((e) => withinDay(e?.row?.forDate, w?.from, w?.to));

/** The entries the window BEFORE this one held. Empty on All, which has none. */
export const inPriorWindow = (entries, w) =>
  (w?.prevFrom ? (entries ?? []).filter((e) => withinDay(e?.row?.forDate, w.prevFrom, w.prevTo)) : []);

// --- the money ----------------------------------------------------------------

/**
 * What the placed bets in a window did.
 *
 * DECIDED BETS ONLY IN THE MONEY. A pending ticket has a stake and no return;
 * carrying it would print a loss nobody has taken yet, and `pending` beside the
 * count is what says the rest of the story. Same rule `summariseDay` states for
 * the paper trail, so the two halves of the screen count alike.
 */
export function moneySummary(entries, w) {
  const list = inWindow(entries, w);
  const decided = list.filter((e) => Number.isFinite(e?.row?.pnl));
  const staked = decided.reduce((a, e) => a + (Number(e.row.stake) || 0), 0);
  const returned = decided.reduce((a, e) => a + (Number(e.row.returned) || 0), 0);
  const won = decided.filter((e) => e.row.result === 'won').length;
  return {
    bets: list.length,
    decided: decided.length,
    pending: list.length - decided.length,
    won,
    strike: decided.length ? won / decided.length : null,
    staked: round2(staked),
    returned: round2(returned),
    net: round2(returned - staked),
  };
}

/**
 * What the PAPER did over a window, in the same shape `moneySummary` answers in.
 *
 * The same rule about decided tickets, for the same reason: a proposal still
 * waiting on a Sunday night game has a printed stake and no return, and carrying
 * it would print a loss the pad has not taken. `pending` beside the count is
 * what says the rest of the story — and here it is every proposal that has not
 * been decided, resulted or not, because on paper those are the same thing.
 *
 * `bets` is the PROPOSED count rather than the decided one, so the field means
 * what it means in `moneySummary` — every ticket the window holds — and
 * `countText` is what a caller prints rather than guessing the noun.
 */
export function paperMoneySummary(byKey) {
  const t = totalAgg(byKey);
  const staked = round2(t.staked);
  const returned = round2(t.returned);
  return {
    bets: t.proposed,
    proposed: t.proposed,
    decided: t.decided,
    pending: Math.max(0, t.proposed - t.decided),
    won: t.won,
    lost: t.lost,
    void: t.void,
    strike: t.decided ? t.won / t.decided : null,
    staked,
    returned,
    net: round2(returned - staked),
    roi: staked > 0 ? (returned - staked) / staked : null,
  };
}

/**
 * The change against the window before, as something a chip can print.
 *
 * A GLYPH, A WORD AND A FIGURE — never the ink on its own. `dir` picks the ink
 * and the ▲/▼/— beside it; `word` is the sr-only reading, because an arrow
 * announced as "black up-pointing triangle" is worse than silence. Null when
 * there is nothing to compare against (All has no prior window), so the caller
 * renders nothing rather than "▲ +$0.00".
 */
export function deltaChip(cur, prev, field, fmt = signedMoney, periodLabel = 'the period before') {
  if (!cur || !prev) return null;
  const a = Number(cur[field]);
  const b = Number(prev[field]);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  const diff = round2(a - b);
  const dir = diff > 0 ? 'up' : diff < 0 ? 'down' : 'flat';
  const word = diff > 0 ? 'up' : diff < 0 ? 'down' : 'level with';
  return {
    dir,
    word,
    diff,
    glyph: diff > 0 ? '▲' : diff < 0 ? '▼' : NO_VALUE,
    figure: fmt(diff),
    periodLabel,
    text: `${fmt(diff)} vs ${periodLabel}`,
    label: `${word} ${fmt(Math.abs(diff))} on ${periodLabel}`,
  };
}

/**
 * The hero's chip, off whichever half of the record the hero is reading.
 *
 * `cur` and `prev` are both `{ placed, proposed }` — the two summaries for this
 * window and for the one before it — so the chip can never end up comparing a
 * paper net against a placed one. That is the whole reason this exists rather
 * than the page picking a pair and calling `deltaChip` itself: the pairing is
 * the part that would be got wrong once and then never noticed, because a chip
 * built out of the wrong two numbers still prints a perfectly plausible figure.
 */
export function modeDelta(mode, cur, prev, w) {
  const which = modeSpec(mode).value;
  return deltaChip(cur?.[which] ?? null, prev?.[which] ?? null, 'net', signedMoney, w?.priorLabel ?? 'the period before');
}

// --- the curve ----------------------------------------------------------------

/**
 * Cumulative net by pad day: `[{ date, x, y, n, readout }]`.
 *
 * A LEADING ZERO POINT, dated the day before the first bet, so the line starts
 * where the money started rather than at the first day's result — a curve whose
 * first point is +$30 has nothing to show a climb against.
 *
 * Downsampled here rather than in the chart: 120 points is more than a phone
 * has pixels for, and a component that thinned its own data would be deciding
 * what the numbers are.
 */
export function equitySeries(entries, w, { maxPoints = 120 } = {}) {
  const decided = inWindow(entries, w)
    .filter((e) => Number.isFinite(e?.row?.pnl) && e?.row?.forDate)
    .sort((a, b) => (a.row.forDate < b.row.forDate ? -1 : a.row.forDate > b.row.forDate ? 1 : 0));
  if (!decided.length) return [];

  const byDay = new Map();
  for (const e of decided) {
    const day = e.row.forDate;
    const at = byDay.get(day) ?? { pnl: 0, n: 0 };
    at.pnl += e.row.pnl;
    at.n += 1;
    byDay.set(day, at);
  }

  const days = [...byDay.keys()].sort();
  const points = [{ date: shiftDate(days[0], -1), y: 0, n: 0 }];
  let net = 0;
  let n = 0;
  for (const day of days) {
    const at = byDay.get(day);
    net = round2(net + at.pnl);
    n += at.n;
    points.push({ date: day, y: net, n });
  }

  const keep = points.length <= maxPoints
    ? points
    // The last point is always kept: it is the figure the readout opens on, and
    // a stride that dropped it would print a curve ending somewhere the tile
    // above it does not.
    : points.filter((_, i) => i % Math.ceil(points.length / maxPoints) === 0 || i === points.length - 1);

  return keep.map((p) => ({
    ...p,
    x: dayShort(p.date),
    readout: `${dayShort(p.date)} · ${signedMoney(p.y)} after ${p.n} bet${p.n === 1 ? '' : 's'}`,
  }));
}

/**
 * A day of the rollup's per-day record, added up: `{ date: { net, decided, proposed } }`.
 *
 * THE ONE PLACE THAT READS THE STORED LETTERS. `insights-rollup.js` keeps an
 * `Agg` as `{p, d, w, l, v, s, r, …}` because it is JSON on a phone and the keys
 * are most of the bytes; everything else in this file works in long names. Two
 * functions indexing `agg.s` is how one of them ends up reading `staked` off a
 * field that means something else.
 */
export function paperDayTotals(paperDaysByDate) {
  const out = {};
  for (const [date, byKey] of Object.entries(paperDaysByDate ?? {})) {
    let staked = 0;
    let returned = 0;
    let decided = 0;
    let proposed = 0;
    for (const agg of Object.values(byKey ?? {})) {
      staked += Number(agg?.s) || 0;
      returned += Number(agg?.r) || 0;
      decided += Number(agg?.d) || 0;
      proposed += Number(agg?.p) || 0;
    }
    out[date] = { net: round2(returned - staked), staked: round2(staked), returned: round2(returned), decided, proposed };
  }
  return out;
}

/**
 * Cumulative PAPER net by pad day, the same shape `equitySeries` answers in:
 * `[{ date, x, y, n, readout }]`.
 *
 * A day that proposed and decided nothing contributes NO POINT rather than a
 * flat one — exactly what `equitySeries` does with a day whose only bets are
 * still pending. A curve with a point on every calendar day would draw a long
 * horizontal run through the days the pad did not settle anything, which reads
 * as a fortnight of breaking even rather than a fortnight of waiting.
 *
 * Same leading zero and the same downsample, for the same reasons written over
 * `equitySeries` — and deliberately not shared with it, because the two read
 * different records and a single function taking a flag is how the placed curve
 * ends up quietly counting a proposal.
 */
export function paperEquitySeries(paperDaysByDate, w, { maxPoints = 120 } = {}) {
  const totals = paperDayTotals(paperDaysByDate);
  const days = Object.keys(totals)
    .filter((date) => withinDay(date, w?.from, w?.to) && totals[date].decided > 0)
    .sort();
  if (!days.length) return [];

  const points = [{ date: shiftDate(days[0], -1), y: 0, n: 0 }];
  let net = 0;
  let n = 0;
  for (const date of days) {
    net = round2(net + totals[date].net);
    n += totals[date].decided;
    points.push({ date, y: net, n });
  }

  const keep = points.length <= maxPoints
    ? points
    : points.filter((_, i) => i % Math.ceil(points.length / maxPoints) === 0 || i === points.length - 1);

  return keep.map((p) => ({
    ...p,
    x: dayShort(p.date),
    readout: `${dayShort(p.date)} · ${signedMoney(p.y)} after ${p.n} ticket${p.n === 1 ? '' : 's'}`,
  }));
}

// --- the heatmap --------------------------------------------------------------

const LEVELS = 4;

/**
 * Twelve weeks of days, Monday first, as `weeks × 7` cells.
 *
 * A cell counts TICKETS DECIDED that day — placed bets and paper proposals
 * together — because the question the grid answers is "how busy was the pad",
 * and a day that proposed nine and had one backed was a busy day.
 *
 * Levels are cut against the busiest day in the grid rather than against a
 * fixed number of tickets: a punter with two bets a week and one with twenty
 * are both reading their own month, not somebody else's.
 *
 * Column-major, which is the order the grid draws in: week by week, Monday to
 * Sunday down each column.
 */
export function heatmapCells(entries, paperDays, { weeks = 12, today, paperNetByDate = {} } = {}) {
  const perDay = new Map();
  const add = (date, n) => { if (date && n) perDay.set(date, (perDay.get(date) ?? 0) + n); };
  for (const e of entries ?? []) {
    if (Number.isFinite(e?.row?.pnl)) add(e.row.forDate, 1);
  }
  for (const [date, n] of Object.entries(paperDays ?? {})) add(date, Number(n) || 0);

  // The grid ends on the Sunday of today's week, so today is never in the last
  // column's top-left corner with six empty days hanging off the end of it.
  const dow = (date) => (new Date(`${date}T00:00:00Z`).getUTCDay() + 6) % 7; // Monday = 0
  const end = shiftDate(today, 6 - dow(today));
  const start = shiftDate(end, -(weeks * 7 - 1));

  const max = Math.max(0, ...[...perDay.entries()]
    .filter(([date]) => withinDay(date, start, today))
    .map(([, n]) => n));

  const cells = [];
  for (let i = 0; i < weeks * 7; i += 1) {
    const date = shiftDate(start, i);
    const future = date > today;
    const tickets = future ? 0 : (perDay.get(date) ?? 0);
    const level = !tickets || !max ? 0 : Math.max(1, Math.ceil((tickets / max) * LEVELS));
    // What the pad's own tickets did that day, whichever mode the screen is in:
    // a cell counts proposals and bets together, so the figure that says how the
    // day went is the paper one. Withheld when nothing settled, rather than
    // printed as a confident $0.00 on a day the pad sat out.
    const paperNet = tickets ? paperNetByDate?.[date] : undefined;
    const netPart = Number.isFinite(paperNet) ? ` · paper ${signedMoney(paperNet)}` : '';
    cells.push({
      date,
      future,
      tickets,
      level,
      paperNet: Number.isFinite(paperNet) ? paperNet : null,
      weekday: i % 7,
      week: Math.floor(i / 7),
      label: future
        ? `${dayShort(date)}, still to come`
        : `${dayShort(date)}, ${tickets} ticket${tickets === 1 ? '' : 's'} decided${netPart}`,
      readout: `${dayShort(date)} · ${tickets} ticket${tickets === 1 ? '' : 's'} decided${netPart}`,
    });
  }
  return cells;
}

// --- the paper trail ----------------------------------------------------------

/** An empty per-key aggregate. The long-named form of the plan's `Agg`. */
export const emptyAgg = () => ({
  proposed: 0,
  decided: 0,
  won: 0,
  lost: 0,
  void: 0,
  staked: 0,
  returned: 0,
  legs: 0,
  legsWithClose: 0,
  clvSum: 0,
  clvSqSum: 0,
  sports: {},
});

const emptySportAgg = () => ({ legs: 0, won: 0, lost: 0, void: 0, legsWithClose: 0, clvSum: 0 });

/** Two aggregates, added. Never mutates either — callers hold both. */
export function mergeAgg(a, b) {
  const out = { ...emptyAgg(), ...(a ?? {}) };
  const from = b ?? {};
  for (const field of ['proposed', 'decided', 'won', 'lost', 'void', 'staked', 'returned', 'legs', 'legsWithClose', 'clvSum', 'clvSqSum']) {
    out[field] = (Number(out[field]) || 0) + (Number(from[field]) || 0);
  }
  out.sports = { ...(a?.sports ?? {}) };
  for (const [code, sport] of Object.entries(from.sports ?? {})) {
    const at = out.sports[code] ? { ...out.sports[code] } : emptySportAgg();
    for (const field of ['legs', 'won', 'lost', 'void', 'legsWithClose', 'clvSum']) {
      at[field] = (Number(at[field]) || 0) + (Number(sport[field]) || 0);
    }
    out.sports[code] = at;
  }
  return out;
}

/** Every key's aggregate, added into one. */
export const totalAgg = (byKey) =>
  Object.values(byKey ?? {}).reduce((acc, agg) => mergeAgg(acc, agg), emptyAgg());

/**
 * The strategy key a proposal or a row belongs to. Classic is split by tier;
 * everything else is its own id. A ticket with no tier stays on the bare id —
 * a pick is not a tier, and `classic-four-ways#null` is not a key.
 *
 * CANONICALISED, so a trail written under a retired id folds in with its heir.
 * The record itself is untouched — the row still carries `optimal-legs` and the
 * frozen `multi.label` still says "Optimal Legs" — but the scoreboard is about
 * a strategy rather than about an id, and three keys where there is now one
 * strategy is thirty days of evidence cut into thirds.
 */
export const keyFor = (strategyId, tier, classicId = 'classic-four-ways') => {
  const id = canonicalStrategyId(strategyId);
  return id === classicId && tier ? `${classicId}#${tier}` : (id ?? classicId);
};

/** The strategy id and tier a key is made of. */
export function splitKey(key) {
  const at = String(key ?? '').indexOf('#');
  return at < 0 ? { strategyId: String(key ?? ''), tier: null } : { strategyId: key.slice(0, at), tier: key.slice(at + 1) };
}

/** Below ten a number is an anecdote; below thirty it is a start. Above that, nothing to add. */
export function sampleNote(n) {
  if (!(n > 0)) return 'Nothing has settled yet';
  if (n < 10) return 'Too few to read anything into';
  if (n < 30) return 'Early days';
  return '';
}

/**
 * What the whole pad has done on paper, and how much of it is allowed to be
 * believed. The verdict is `verdictFrom` — the SAME thresholds a strategy card
 * and a backtest are held to, imported rather than restated, because a screen
 * with its own thresholds is a screen that claims an edge the rest of the app
 * would refuse.
 */
export function paperSummary(byKey) {
  const t = totalAgg(byKey);
  const avgClv = t.legsWithClose ? t.clvSum / t.legsWithClose : null;
  const clvInterval = meanInterval({ n: t.legsWithClose, sum: t.clvSum, sumSq: t.clvSqSum });
  const strike = t.decided ? t.won / t.decided : null;
  const roi = t.staked > 0 ? (t.returned - t.staked) / t.staked : null;
  const verdict = verdictFrom(t.decided, t.legsWithClose, avgClv, clvInterval);
  return {
    ...t,
    avgClv,
    clvInterval,
    strike,
    roi,
    net: round2(t.returned - t.staked),
    verdict,
    verdictLabel: verdictText(verdict),
    note: sampleNote(t.decided),
  };
}

/**
 * The fewest closed legs a CLV figure may be PRINTED over.
 *
 * Not `EVIDENCE_MIN_LEGS`, and deliberately far below it: that is the bar for
 * BELIEVING a figure, and this is the bar for showing one at all. Between the
 * two the tile prints the number with its `n` beside it and the verdict under
 * the block says what it is worth; below this one there is no number worth
 * printing, because a single lucky leg would swing it by ten points.
 */
export const CLV_MIN_LEGS = 10;

/**
 * THE ONE LINE UNDER THE HEADLINE, and the reason the headline is the headline.
 *
 * Profit is the number everybody wants and the slowest honest signal there is:
 * at a 2% edge and typical multi variance it takes about a thousand bets before
 * a winning record can be told from a lucky one, which is years of weekends.
 * Beating the close is the same claim measured against something that moves —
 * the market's own final answer — and it separates skill from luck in
 * hundreds of legs rather than thousands of bets. So it goes first, and this
 * sentence says why in the words a person would use.
 */
export const CLV_NOTE = 'Beating the close is the earliest honest sign; profit takes a thousand bets to tell.';

/**
 * The headline: what the pad's prices were worth against the close, over how
 * many legs, give or take how much.
 *
 * THE INTERVAL IS PART OF THE FIGURE, not a footnote to it. "+1.8%" alone is a
 * claim; "+1.8% vs the close · 214 legs · ±0.9%" is a measurement, and the two
 * read completely differently to somebody deciding whether to keep going. The
 * half-width is a 95% normal interval on the mean per-leg CLV (`meanInterval`),
 * so ±0.9 on +1.8 says the pad is ahead and ±2.4 on +1.8 says it has shown
 * nothing yet — which is exactly the call `verdictFrom` makes underneath.
 */
function clvTile(legsWithClose, avgClv, interval) {
  // "Average per leg", under a sheet already titled The close and above a sub
  // line that says what it is measured against: the label's job is the one
  // thing neither of them says, which is that this is a PER-LEG mean and not a
  // figure about tickets (reviewer round 2, point 4).
  const label = 'Average per leg';
  if (legsWithClose < CLV_MIN_LEGS) {
    return { key: 'clv', label, value: NO_VALUE, sub: `needs ${CLV_MIN_LEGS} settled legs`, note: CLV_NOTE };
  }
  // Always plural past ten, so no "1 leg" branch to keep right.
  const sub = [
    'vs the close',
    `${legsWithClose} legs`,
    interval ? `±${pct(interval.half)}` : null,
  ].filter(Boolean).join(' · ');
  return { key: 'clv', label, value: signedPct(avgClv), sub, note: CLV_NOTE };
}

/**
 * One row's CLV, as something a list can print and colour: `{ text, ink,
 * ahead }`, or null where there is nothing measurable to say.
 *
 * TWO INKS, NEVER THREE. A row whose interval clears zero is ahead of the
 * market and prints in `--go`; everything else — behind, level, or too wide to
 * tell the difference — prints in carbon. There is no red, deliberately: a red
 * row would read as a warning about the strategy when most of them are simply
 * rows with not enough legs on them yet, and the app does not stamp what it
 * cannot call. The interval is in the text either way, which is the reading
 * that survives being colour-blind, printed, or screenshotted.
 */
export function clvCell(avgClv, legsWithClose, interval) {
  const figure = clvFigure(avgClv, legsWithClose, interval);
  return figure ? { ...figure, text: `CLV ${figure.text}` } : null;
}

/**
 * THE SAME FIGURE WITHOUT THE WORD "CLV" IN FRONT OF IT — "+1.2% ±0.2% over 183
 * legs" — for the two places that have already said what it is about.
 *
 * A best bet prints it under a sentence naming the strategy, and Today's sheet
 * prints it under "Your record vs the close"; repeating the acronym there would
 * be the label said twice. Same ink rule as `clvCell` and the same interval,
 * because it is the same measurement: a row whose interval clears zero is ahead
 * of the market, and everything else prints in carbon and claims nothing.
 */
export function clvFigure(avgClv, legsWithClose, interval) {
  if (!legsWithClose || !Number.isFinite(avgClv)) return null;
  const ahead = !!interval && interval.lo > 0;
  return {
    ahead,
    ink: ahead ? 'var(--go)' : 'var(--carbon)',
    text: [
      signedPct(avgClv),
      interval ? `±${pct(interval.half)}` : null,
      `over ${legsWithClose} leg${legsWithClose === 1 ? '' : 's'}`,
    ].filter(Boolean).join(' '),
  };
}

/** What a strategy's key is worth against the close, off one `Agg` from the trail. */
export function clvFor(agg) {
  const legs = Number(agg?.legsWithClose) || 0;
  if (!legs) return null;
  const avgClv = agg.clvSum / legs;
  return clvFigure(avgClv, legs, meanInterval({ n: legs, sum: agg.clvSum, sumSq: agg.clvSqSum }));
}

/** The strip at the top of Today's Best bets section. */
export const RECORD_VS_CLOSE = 'Your record vs the close';

/**
 * And what that strip says before there is a record to print.
 *
 * NOT SILENCE. A section that printed its record on the mornings it had one and
 * nothing at all on the mornings it did not would be at its quietest exactly
 * when a reader most needs telling that the app is counting — which is the
 * first fortnight, when nothing has settled and nothing has cleared. So the
 * strip is always there and the count is in it: "12 legs settled" is a number
 * that goes up, and a number going up is the only evidence a bar that has not
 * been cleared yet can honestly offer.
 */
export const NO_RECORD_YET = 'No record vs the close yet';

/** The row that scores the gate itself. See `lib/rejections.js`. */
export const REFUSED_LABEL = 'What the bar refused';

/**
 * ONE LINE THAT AUDITS THE BAR: what the legs the gate turned down were worth
 * against the close, with their own n and interval.
 *
 * GREEN ONLY WHEN THE WHOLE INTERVAL IS ABOVE ZERO, and green here means
 * something different from green anywhere else in the app: it is not good news.
 * It says the refused legs were beating the market, which is the bar costing
 * money — the one reading worth acting on, and the reason this row is worth
 * printing at all. Everything else is carbon and claims nothing, which is
 * almost always the honest answer.
 *
 * The sub-line says which way to read it, because a colour that means the
 * opposite of what it means three sheets up needs saying out loud.
 */
export function refusedRow(agg) {
  const legs = Number(agg?.legsWithClose) || 0;
  const refused = Number(agg?.legs) || 0;
  if (!legs) {
    return refused
      ? { text: `${REFUSED_LABEL}: ${refused} leg${refused === 1 ? '' : 's'}, none settled against a close yet`, ink: 'var(--carbon)', costly: false }
      : null;
  }
  const avgClv = agg.clvSum / legs;
  const interval = meanInterval({ n: legs, sum: agg.clvSum, sumSq: agg.clvSqSum });
  const costly = !!interval && interval.lo > 0;
  const figure = clvFigure(avgClv, legs, interval);
  return {
    costly,
    ink: costly ? 'var(--go)' : 'var(--carbon)',
    text: `${REFUSED_LABEL}: ${figure.text}`,
    note: costly
      ? 'Those legs beat the close. The bar is costing money, not saving it.'
      : 'Nothing here says the bar is set wrong.',
  };
}

/**
 * "Your record vs the close: +1.2% ±0.4% over 183 legs", off the same summary
 * the Insights headline is read from — so the two screens cannot print two
 * different records on one morning. Null under `CLV_MIN_LEGS`, where there is
 * no number worth quoting and the sheet says nothing rather than something
 * small and loud.
 */
export function recordVsClose(paper) {
  const legs = Number(paper?.legsWithClose) || 0;
  if (legs < CLV_MIN_LEGS) return null;
  const figure = clvFigure(paper.avgClv, legs, paper.clvInterval);
  return figure ? { ...figure, text: `${RECORD_VS_CLOSE}: ${figure.text}` } : null;
}

/**
 * The same line, and a sentence for the morning it cannot be read yet:
 * "No record vs the close yet · 12 legs settled".
 *
 * `recordVsClose` answers null there, which is right for the share card (a
 * picture headed by a claim about nothing is worse than a picture without one)
 * and wrong for the screen, where the strip is how a reader FINDS the section.
 * So the two readings are two functions over one measurement rather than one
 * function with an opinion about who is asking.
 */
export function recordStrip(paper) {
  const record = recordVsClose(paper);
  if (record) return record;
  const legs = Number(paper?.legsWithClose) || 0;
  return {
    ahead: false,
    ink: 'var(--carbon-faint)',
    text: `${NO_RECORD_YET} · ${legs} leg${legs === 1 ? '' : 's'} settled`,
  };
}

// --- the scoreboard -----------------------------------------------------------

/**
 * One row per strategy key: the actual money beside what the paper says.
 *
 * EVERY KEY WITH EITHER GETS A ROW. A strategy that proposed all fortnight and
 * was never backed still belongs on the board — that is the row that says "the
 * pad liked this and you did not" — and a bet placed on a strategy with no
 * paper still shows its money.
 *
 * `fraction` is measured against the biggest ABSOLUTE net on the board, so a
 * −$30 row and a +$30 row draw the same length bar in opposite readings, and
 * `negative` is what tells the bar to print as an outline rather than as ink.
 *
 * IN PROPOSED MODE THE BAR IS THE PAPER NET, and the placed figures move into
 * the sub line — "2 placed · actual +$15.20". The rows, the labels and the
 * arithmetic are the same either way; what changes is which of the two nets the
 * board is sorted, scaled and printed by, because a board sorted by money while
 * the hero above it reads paper is a board that answers a different question
 * from the screen it is on.
 */
export function scoreboardRows(entries, byKey, { classicId = 'classic-four-ways', names = {}, tierLabel = {}, mode = 'placed' } = {}) {
  const proposedMode = modeSpec(mode).value === 'proposed';
  const rows = new Map();
  const at = (key) => {
    if (!rows.has(key)) {
      const { strategyId, tier } = splitKey(key);
      rows.set(key, {
        key,
        strategyId,
        tier,
        name: names[strategyId] ?? strategyId,
        bets: 0,
        decided: 0,
        won: 0,
        staked: 0,
        returned: 0,
        net: 0,
      });
    }
    return rows.get(key);
  };

  for (const e of entries ?? []) {
    const row = at(keyFor(e?.row?.strategyId, e?.row?.tier, classicId));
    if (e?.row?.strategy) row.name = names[row.strategyId] ?? e.row.strategy;
    row.bets += 1;
    if (Number.isFinite(e?.row?.pnl)) {
      row.decided += 1;
      row.staked += Number(e.row.stake) || 0;
      row.returned += Number(e.row.returned) || 0;
      row.net = round2(row.net + e.row.pnl);
      if (e.row.result === 'won') row.won += 1;
    }
  }
  for (const key of Object.keys(byKey ?? {})) at(key);

  const out = [...rows.values()].map((row) => {
    const paper = byKey?.[row.key] ?? emptyAgg();
    const paperNet = round2((Number(paper.returned) || 0) - (Number(paper.staked) || 0));
    const avgClv = paper.legsWithClose ? paper.clvSum / paper.legsWithClose : null;
    const clvInterval = meanInterval({ n: paper.legsWithClose, sum: paper.clvSum, sumSq: paper.clvSqSum });
    const clv = clvCell(avgClv, paper.legsWithClose, clvInterval);
    const paperStrike = paper.decided ? paper.won / paper.decided : null;
    const placedStrike = row.decided ? row.won / row.decided : null;
    // Proposed reads the paper strike first and the placed one only as a
    // fallback; placed reads them the other way round. Either way a row prints
    // the strike of whichever record its figure is about.
    const strike = proposedMode ? (paperStrike ?? placedStrike) : (placedStrike ?? paperStrike);
    const sort = proposedMode ? paperNet : row.net;
    return {
      ...row,
      label: row.tier ? `${row.name} · ${tierLabel[row.tier] ?? row.tier}` : row.name,
      paperNet,
      paperDecided: paper.decided,
      paperProposed: paper.proposed,
      avgClv,
      clvInterval,
      // Out of `sub` and on its own, because it is the one part of a row that
      // is allowed ink of its own: see `clvCell`.
      clv,
      legsWithClose: paper.legsWithClose,
      strike,
      sort,
      value: signedMoney(sort),
      negative: sort < 0,
      sub: (proposedMode
        ? [
          paper.proposed ? `${paper.proposed} proposed` : 'None proposed',
          strike === null ? null : `strike ${pct(strike)}`,
          row.bets ? `${row.bets} placed · actual ${signedMoney(row.net)}` : 'none placed',
        ]
        : [
          row.bets ? `${row.bets} bet${row.bets === 1 ? '' : 's'}` : 'No bets yet',
          strike === null ? null : `strike ${pct(strike)}`,
          paper.decided ? `paper ${signedMoney(paperNet)}` : null,
        ]).filter(Boolean).join(' · '),
    };
  });

  const max = Math.max(0, ...out.map((r) => Math.abs(r.sort)));
  return out
    .map((r) => ({ ...r, fraction: max > 0 ? Math.abs(r.sort) / max : 0 }))
    .sort((a, b) => b.sort - a.sort || a.label.localeCompare(b.label));
}

// --- proposed → placed → won ---------------------------------------------------

/**
 * The three bands, and the two percentages between them.
 *
 * THE MIDDLE BAND IS A REAL JOIN, not a count of bets that happen to be in the
 * window: a proposal and a bet are the same ticket when they share `multi.id`
 * (`proposals.js` ↔ `bet.multiId`), and `proposedIds` is that set. Without it
 * there is nothing honest to say about how many proposals were backed, so the
 * band falls back to counting bets and `joined` says the funnel is only
 * approximate.
 */
export function funnelStages(entries, byKey, w, { proposedIds = null } = {}) {
  const t = totalAgg(byKey);
  const list = inWindow(entries, w);
  const ids = proposedIds instanceof Set ? proposedIds : (Array.isArray(proposedIds) ? new Set(proposedIds) : null);
  const placed = ids ? list.filter((e) => ids.has(e?.row?.multiId)) : list;
  const won = placed.filter((e) => e?.row?.result === 'won');

  const share = (n, of) => (of > 0 ? n / of : null);
  const stages = [
    { key: 'proposed', label: 'Proposed', n: t.proposed, of: null, share: null },
    { key: 'placed', label: 'Placed', n: placed.length, of: t.proposed, share: share(placed.length, t.proposed) },
    { key: 'won', label: 'Won', n: won.length, of: placed.length, share: share(won.length, placed.length) },
  ];

  const actualNet = round2(placed.reduce((a, e) => a + (Number.isFinite(e?.row?.pnl) ? e.row.pnl : 0), 0));
  const paperNet = round2(t.returned - t.staked);
  const widest = Math.max(1, ...stages.map((s) => s.n));

  return {
    joined: !!ids,
    stages: stages.map((s) => ({
      ...s,
      // A band is never narrower than a fifth of the widest: a stage of one
      // ticket out of ninety is a sliver with a number that will not fit in it,
      // and the text is what carries the arithmetic anyway.
      fraction: Math.max(0.2, s.n / widest),
      text: s.share === null ? `${s.n}` : `${s.n} · ${pct(s.share)} of ${s.of}`,
    })),
    foot: `Paper net at printed stakes ${signedMoney(paperNet)} · Actual net ${signedMoney(actualNet)}`,
    paperNet,
    actualNet,
  };
}

// --- by sport ------------------------------------------------------------------

/**
 * The paper trail's LEGS by sport, which is the larger sample: a fortnight is
 * thirty-odd tickets and a hundred and forty legs, and a table of sports cut by
 * ticket would have one row with any numbers on it.
 *
 * Top `max`, then everything else added into one "Other" row rather than
 * dropped — a table whose rows do not add up to the total above it is a table
 * somebody has to reconcile by hand.
 */
export function sportRows(byKey, { max = 8 } = {}) {
  const t = totalAgg(byKey);
  const all = Object.entries(t.sports ?? {}).map(([code, s]) => ({ code, ...s }));
  all.sort((a, b) => b.legs - a.legs || a.code.localeCompare(b.code));

  const head = all.slice(0, max);
  const tail = all.slice(max);
  if (tail.length) {
    head.push(tail.reduce((acc, s) => ({
      code: 'Other',
      legs: acc.legs + s.legs,
      won: acc.won + s.won,
      lost: acc.lost + s.lost,
      void: acc.void + s.void,
      legsWithClose: acc.legsWithClose + s.legsWithClose,
      clvSum: acc.clvSum + s.clvSum,
    }), { code: 'Other', ...emptySportAgg() }));
  }

  const widest = Math.max(1, ...head.map((s) => s.legs));
  return head.map((s) => {
    const decided = s.won + s.lost;
    const strike = decided ? s.won / decided : null;
    const avgClv = s.legsWithClose ? s.clvSum / s.legsWithClose : null;
    return {
      key: s.code,
      label: s.code,
      legs: s.legs,
      decided,
      strike,
      avgClv,
      value: strike === null ? NO_VALUE : pct(strike),
      fraction: s.legs / widest,
      negative: false,
      // No interval on a sport row: the fold keeps no square per sport (see
      // `emptyAgg` in insights-rollup.js), so the figure prints with its `n`
      // and carbon ink and makes no claim of its own.
      clv: clvCell(avgClv, s.legsWithClose, null),
      // The CLV cell carries its own denominator, so the row only states the
      // leg count where the two differ — a row reading "171 legs · CLV +0.9%
      // over 171 legs" is the same number printed twice.
      sub: [
        s.legsWithClose === s.legs ? null : `${s.legs} leg${s.legs === 1 ? '' : 's'}`,
        s.legsWithClose ? null : 'no closing prices',
      ].filter(Boolean).join(' · '),
    };
  });
}

// --- nothing to read yet --------------------------------------------------------

/**
 * Which empty state the screen is in, or null when it has something to say.
 *
 *   'nothing'   no bets and no paper — the whole screen collapses to one line.
 *   'no-bets'   the pad has a record; nobody has backed anything. The paper
 *               sheets still print, and only the money blocks collapse. Placed
 *               mode only: in Proposed the money block IS the paper record, and
 *               a screen full of figures does not have an empty state.
 *   'no-trail'  Proposed mode with bets but no paper yet — the trail settles
 *               the next morning, and that is what the line says.
 */
export function emptyKey(entries, byKey, mode = 'placed') {
  const bets = (entries ?? []).length;
  const proposed = totalAgg(byKey).proposed;
  if (!bets && !proposed) return 'nothing';
  if (modeSpec(mode).value === 'proposed') return proposed ? null : 'no-trail';
  return bets ? null : 'no-bets';
}

/** The one line an empty screen prints. */
export const EMPTY_TEXT = {
  nothing: 'Nothing to read yet. Place a bet on Today, or let the paper trail build for a few days',
  'no-bets': 'No bets placed yet. What follows is what the pad proposed, settled the next morning whether or not a cent went on it',
  'no-trail': 'Let the pad run for a day: every ticket it proposes is settled here the next morning, placed or not',
};

// --- the page ------------------------------------------------------------------

/**
 * The whole screen as a plain object: every figure already formatted, every
 * list already sorted. The page binds to this and does no arithmetic of its
 * own, which is what lets the whole of Insights be tested without a browser.
 */
export function insightsModel({
  entries = [],
  byKey = {},
  // What the gate refused over the same window, as `refusedAgg` counts it.
  // Absent on a device that has never refused anything, which is every device
  // until the day after the first pad.
  refused = null,
  byKeyPrev = {},
  paperDays = {},
  paperDaysByDate = {},
  proposedIds = null,
  range = '30d',
  mode = 'proposed',
  today,
  names = {},
  tierLabel = {},
  classicId = 'classic-four-ways',
  weeks = 12,
} = {}) {
  const w = windowFor(range, today);
  const m = modeSpec(mode);
  const proposedMode = m.value === 'proposed';

  // Both summaries, both windows, always. They cost two passes over a list that
  // is already in memory, and holding both is what lets the chip, the sub line
  // and the scoreboard agree about which record they are reading — see
  // `modeDelta`.
  const cur = { placed: moneySummary(entries, w), proposed: paperMoneySummary(byKey) };
  const prior = w.prevFrom
    ? { placed: moneySummary(entries, { from: w.prevFrom, to: w.prevTo }), proposed: paperMoneySummary(byKeyPrev) }
    : null;
  const hero = cur[m.value];
  const paper = paperSummary(byKey);
  const dayTotals = paperDayTotals(paperDaysByDate);
  const paperNetByDate = Object.fromEntries(Object.entries(dayTotals).map(([date, t]) => [date, t.net]));

  const countText = proposedMode
    ? `${hero.bets} ticket${hero.bets === 1 ? '' : 's'}`
    : `${hero.bets} bet${hero.bets === 1 ? '' : 's'}`;

  return {
    window: w,
    rangeLabel: w.title,
    mode: m.value,
    modeLabel: m.label,
    modeExplainer: MODE_EXPLAINER[m.value],
    empty: emptyKey(entries, byKey, m.value),
    net: {
      mode: m.value,
      // "Paper net" is not a longer way of saying "Net": one is what the pad
      // would have made and the other is what the punter did, and a hero that
      // did not say which is a figure somebody will quote at a pub.
      label: proposedMode ? 'Paper net' : 'Net',
      stakeNote: STAKE_NOTE[m.value],
      countText,
      value: signedMoney(hero.net),
      sub: `${money(hero.staked)} staked ${STAKE_NOTE[m.value]} · ${money(hero.returned)} returned · ${countText}${hero.pending ? ` (${hero.pending} pending)` : ''}`,
      delta: modeDelta(m.value, cur, prior, w),
      ...hero,
    },
    close: {
      // THE FIRST FIGURE ON THE SCREEN, and the only one at hero size.
      //
      // A CLV FIGURE UNDER TEN LEGS IS NOISE WITH A SIGN ON IT. The rest of
      // the app already refuses to call ten legs an edge (`verdictFrom`), and
      // a tile printing "+8.4%" over three of them is the same claim made
      // louder — the figure is what gets quoted, not the verdict under it. So
      // the figure is withheld until the denominator can carry it, and the
      // sub-line says exactly what it is waiting for. Above the bar the
      // denominator and the interval ride with the figure, which is the whole
      // point of a sub-line: a CLV nobody can see the `n` of is a CLV nobody
      // can weigh.
      headline: clvTile(paper.legsWithClose, paper.avgClv, paper.clvInterval),
      note: CLV_NOTE,
      tiles: [
        { key: 'strike', label: 'Strike', value: paper.strike === null ? NO_VALUE : pct(paper.strike), sub: `${paper.won} of ${paper.decided} decided` },
        { key: 'roi', label: 'Paper ROI', value: paper.roi === null ? NO_VALUE : signedPct(paper.roi), sub: 'at printed stakes' },
        { key: 'proposed', label: 'Proposed', value: String(paper.proposed), sub: `${paper.won} won · ${paper.lost} lost · ${paper.void} void` },
      ],
      verdict: paper.verdict,
      verdictLabel: paper.verdictLabel,
      // Two different sentences, and the names say which is which: `note` is
      // why this figure is the headline (the same words on every screen that
      // prints it), `sampleNote` is what THIS sample is worth.
      sampleNote: paper.note,
      stamp: paper.verdict === 'no-edge',
    },
    curve: proposedMode ? paperEquitySeries(paperDaysByDate, w) : equitySeries(entries, w),
    curveLabel: proposedMode ? 'Cumulative paper net' : 'Cumulative net',
    heat: heatmapCells(entries, paperDays, { weeks, today, paperNetByDate }),
    // THE OTHER HALF OF THE RECORD, under the close block: what the bar turned
    // down, scored the same way the bar's own tickets are. Null where nothing
    // has been refused, so a screen with no gate history prints no row.
    refused: refusedRow(refused),
    scoreboard: scoreboardRows(inWindow(entries, w), byKey, { classicId, names, tierLabel, mode: m.value }),
    funnel: funnelStages(entries, byKey, w, { proposedIds }),
    sports: sportRows(byKey),
  };
}
